import { createApi } from "@reduxjs/toolkit/query/react";

import customBaseQuery from "../../../utils/customBaseQuery";
import { gql } from "graphql-request";

export const AssessmentApiSlice = createApi({
  reducerPath: "AssessmentApiSlice",
  baseQuery: customBaseQuery,
  tagTypes: ["SavedCodes"],
  endpoints: (builder) => ({
    getAssessmentOngoingPre: builder.query({
      query: ({ scheduleId }: { scheduleId: string }) => ({
        url: `/assessment/ongoing/pre/${scheduleId}`,
        method: "POST"
      }),
      extraOptions: { service: "Assessment" }
    }),
    getAssessmentOngoing: builder.query({
      query: ({
        scheduleId,
        ongoingId
      }: {
        scheduleId: string;
        ongoingId: string;
      }) => ({
        url: `/assessment/ongoing/${scheduleId}/${ongoingId}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    finishOngoingAssessment: builder.query({
      query: ({
        scheduleId,
        ongoingId
      }: {
        scheduleId: string;
        ongoingId: string;
      }) => ({
        url: `/assessment/ongoing/finish/${scheduleId}/${ongoingId}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    checkAssessmentAccess: builder.query({
      query: ({ scheduleId }: { scheduleId: string }) => ({
        url: `/assessment/ongoing/candidate/access/${scheduleId}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    startAssessment: builder.mutation({
      query: ({
        scheduleId,
        tncAccepted,
        tncAcceptedAt
      }: {
        scheduleId: string;
        tncAccepted: boolean;
        tncAcceptedAt: string | null;
      }) => ({
        url: `/assessment/ongoing/candidate/${scheduleId}`,
        method: "POST",
        body: { scheduleId, tncAccepted, tncAcceptedAt }
      }),
      extraOptions: { service: "Assessment" }
    }),
    startSection: builder.mutation({
      query: ({
        sectionId,
        ongoingId,
        actionType
      }: {
        sectionId: string;
        ongoingId: string;
        actionType: "started" | "completed";
      }) => ({
        url: `/assessment/ongoing/section/${actionType}/${ongoingId}/${sectionId}`,
        method: "POST",
        body: {}
      }),
      extraOptions: { service: "Assessment" }
    }),
    startQuestion: builder.mutation({
      query: ({
        questionId,
        ongoingId,
        actionType
      }: {
        questionId: string;
        ongoingId: string;
        actionType: "started" | "completed";
      }) => ({
        url: `/assessment/ongoing/question/${actionType}/${ongoingId}/${questionId}`,
        method: "POST",
        body: {}
      }),
      extraOptions: { service: "Assessment" }
    }),
    attemptQuestion: builder.mutation({
      query: ({
        questionId,
        ongoingId,
        body
      }: {
        questionId: string;
        ongoingId: string;
        body: any;
      }) => ({
        url: `/assessment/ongoing/attempt/${ongoingId}/${questionId}`,
        method: "POST",
        body: body
      }),
      extraOptions: { service: "Assessment" }
    }),
    flagQuestion: builder.mutation({
      query: ({
        questionId,
        ongoingId,
        flag
      }: {
        questionId: string;
        ongoingId: string;
        flag: "true" | "false";
      }) => ({
        url: `/assessment/ongoing/flag/${ongoingId}/${questionId}/${flag}`,
        method: "POST"
      }),
      extraOptions: { service: "Assessment" }
    }),
    timeSpent: builder.mutation({
      query: ({
        ongoingId,
        body
      }: {
        ongoingId: string;
        body: {
          sectionId: string | null;
          questionid: string | null;
          secOrQuestion: "section" | "question";
          timeSpent: number;
        };
      }) => ({
        url: `/assessment/ongoing/timespent/${ongoingId}`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    }),
    proctoringApi: builder.mutation({
      query: ({
        ongoingId,
        scheduleId,
        violationType,
        body
      }: {
        ongoingId: string;
        scheduleId: string;
        violationType: string;
        body: any;
      }) => ({
        url: `/proctoring/analytics/save/${ongoingId}/${scheduleId}/${violationType}`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    }),
    getImageUrl: builder.query({
      query: ({
        ongoingId,
        scheduleId
      }: {
        ongoingId: string;
        scheduleId: string;
      }) => ({
        url: `/asset/upload/${ongoingId}/${scheduleId}/jpg`,
        method: "GET"
      }),
      extraOptions: { service: "Proctoring_Image" }
    }),
    uploadProctoringEvent: builder.mutation({
      query: ({
        image,
        ongoingId,
        scheduleId,
        detectedViolation,
        skipProcessing
      }: {
        image: string;
        ongoingId: string;
        scheduleId: string;
        detectedViolation: string;
        skipProcessing: boolean;
      }) => ({
        url: `/proctoring/event`,
        method: "POST",
        body: {
          image,
          ongoing_id: ongoingId,
          schedule_id: scheduleId,
          detected_violation: detectedViolation,
          skip_processing: skipProcessing
        }
      }),
      extraOptions: { service: "Proctoring" }
    }),
    getSampleTestCases: builder.query({
      query: ({
        ongoingId,
        questionId,
        questionVersion
      }: {
        ongoingId: string;
        questionId: string;
        questionVersion: string | number;
      }) => ({
        url: `/assessment/ongoing/testcases/${ongoingId}/${questionId}/${questionVersion}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    saveCode: builder.mutation({
      query: ({
        ongoingId,
        questionId,
        quesVersion,
        body
      }: {
        ongoingId: string;
        questionId: string;
        quesVersion: number;
        body: any;
      }) => ({
        url: `/ongoing/save-code/${ongoingId}/${questionId}/${quesVersion}`,
        method: "POST",
        body
      }),
      invalidatesTags: (result, error, { ongoingId, questionId }) => [
        { type: "SavedCodes", id: `${ongoingId}-${questionId}` }
      ],
      extraOptions: { service: "Assessment" }
    }),
    executeTestCase: builder.mutation({
      query: ({ ongoingId, body }: { ongoingId: string; body: any }) => ({
        url: `/ongoing/execute-code/${ongoingId}`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    }),
    getSavedCodes: builder.query({
      query: ({
        ongoingId,
        questionId
      }: {
        ongoingId: any;
        questionId: string;
      }) => ({
        url: `/ongoing/get-latest-code/${ongoingId}/${questionId}`,
        method: "GET"
      }),
      providesTags: (result, error, { ongoingId, questionId }) => [
        { type: "SavedCodes", id: `${ongoingId}-${questionId}` }
      ],
      extraOptions: { service: "Assessment" }
    }),
    getSaveSubjectiveQL: builder.query({
      query: ({ ongoingId, questionId, questionVersion }: any) => ({
        url: "/graphql",
        body: gql`query {
        getSaveSubjectiveQL(
        request: { ongoingId: "${ongoingId}", 
        questionId: "${questionId}", questionVersion: ${questionVersion},
        pageNumber: 1 ,
        fetchType:"LATEST"}
    ) {
        data {
            pageNo
            pageSize
            total
            savedSubjective {
                ongoingId
                questionId
                questionVersion
                rubricId
                obtainedMarks
                totalMarks
                solution
                usersubmission 
                submissionType
                fileDetails
            }
            latestSubmissionId
        }
    }           
                }`
      }),
      extraOptions: { service: "Assessment" }
    }),
    saveSubjective: builder.mutation({
      query: ({
        ongoingId,
        questionId,
        quesVersion,
        body
      }: {
        ongoingId: string;
        questionId: string;
        quesVersion: number;
        body: any;
      }) => ({
        url: `/ongoing/save-subjective/${ongoingId}/${questionId}/${quesVersion}`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    }),
    getSavedVideo: builder.mutation({
      query: (body: any) => ({
        url: `/subjective/assets/upload/url`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    }),
    heartBeatApi: builder.query({
      query: ({
        scheduleId,
        ongoingId
      }: {
        scheduleId: string;
        ongoingId: string;
      }) => ({
        url: `/assessment/ongoing/heartbeat/${scheduleId}/${ongoingId}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    getCommonConfigData: builder.query({
      query: (key: string) => ({
        url: `/common-config/${key}`,
        method: "GET"
      }),
      extraOptions: { service: "Utility" }
    }),
    getAssessmentOngoingStats: builder.query({
      query: (scheduleId: string) => ({
        url: `/assessment/ongoing/stats/${scheduleId}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    getUploadConfig: builder.query({
      query: ({
        questionId,
        quesVersion
      }: {
        questionId: string;
        quesVersion: number;
      }) => ({
        url: `/subjective/upload/${questionId}/${quesVersion}`,
        method: "GET"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getAttemptsRemaining: builder.query({
      query: (params: any) => ({
        url: `/ongoing/count`,
        method: "GET",
        params: params
      }),
      extraOptions: { service: "Assessment" }
    }),
    updateSessionId: builder.mutation({
      query: (body: any) => ({
        url: `/assessment/ongoing/update/sessionId`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    }),
    getAssessmentResultStatus: builder.query({
      query: (ongoingId: string) => ({
        url: `/assessment/ongoing/result/status/${ongoingId}`,
        method: "GET"
      }),
      extraOptions: { service: "Assessment" }
    }),
    resetCodingQuestion: builder.mutation({
      query: (body: any) => ({
        url: `/assessment/ongoing/reset-codingQn`,
        method: "POST",
        body
      }),
      invalidatesTags: (result, error, { ongoingId, questionId }) => [
        { type: "SavedCodes", id: `${ongoingId}-${questionId}` }
      ],
      extraOptions: { service: "Assessment" }
    }),
    resetSubjectiveQuestion: builder.mutation({
      query: (body: any) => ({
        url: `/assessment/ongoing/reset-subjectiveQn`,
        method: "POST",
        body
      }),
      extraOptions: { service: "Assessment" }
    })
  })
});

export const {
  // ******Queries**********
  //
  // ---  getAssessmentOngoingPre
  useGetAssessmentOngoingPreQuery,
  useLazyGetAssessmentOngoingPreQuery,
  // ---  getAssessmentOngoing
  useGetAssessmentOngoingQuery,
  useLazyGetAssessmentOngoingQuery,
  //  --- Finish Assessment
  useFinishOngoingAssessmentQuery,
  useLazyFinishOngoingAssessmentQuery,
  //  --- Access Check
  useCheckAssessmentAccessQuery,
  useLazyCheckAssessmentAccessQuery,
  // get image url
  useLazyGetImageUrlQuery,
  //
  // ******Mutations*******
  //
  //  ---  Start Assessment
  useStartAssessmentMutation,
  //  --- Start Section
  useStartSectionMutation,
  //  --- Start Question
  useStartQuestionMutation,
  // ---  Attempt Question
  useAttemptQuestionMutation,
  //  --- Flag Question
  useFlagQuestionMutation,
  //  --- Time Spent
  useTimeSpentMutation,
  // --- Proctoring
  useProctoringApiMutation,
  // --- Upload Proctoring Event
  useUploadProctoringEventMutation,
  // --- Get sample test cases
  useGetSampleTestCasesQuery,
  // --- To save the code added
  useSaveCodeMutation,
  // --- To execute sample test cases
  useExecuteTestCaseMutation,
  // --- To get saved codes
  useGetSavedCodesQuery,
  useGetSaveSubjectiveQLQuery,
  useSaveSubjectiveMutation,
  useLazyHeartBeatApiQuery,
  useHeartBeatApiQuery,
  useGetCommonConfigDataQuery,
  useLazyGetCommonConfigDataQuery,
  useGetAssessmentOngoingStatsQuery,
  useGetUploadConfigQuery,
  useGetSavedVideoMutation,
  useGetAttemptsRemainingQuery,
  useUpdateSessionIdMutation,
  useLazyGetAssessmentResultStatusQuery,
  useResetCodingQuestionMutation,
  useResetSubjectiveQuestionMutation
} = AssessmentApiSlice;
